import { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { useRole } from "@/store/role";
import { hasValidAccessToken, loginRedirectPath } from "@/lib/auth";
import { effectiveRoutePermissions, firstAllowedPath } from "@/config/navigation";

function AccessDenied() {
  return (
    <div
      className="min-h-[50vh] flex flex-col items-center justify-center text-center px-6"
      data-testid="access-denied"
    >
      <div className="h-14 w-14 rounded-2xl bg-mahindra-crimson/10 flex items-center justify-center mb-4">
        <ShieldOff className="h-7 w-7 text-mahindra-crimson" strokeWidth={2} />
      </div>
      <h1 className="font-display text-xl font-bold text-mahindra-ink">Access denied</h1>
      <p className="mt-2 text-sm text-mahindra-steel max-w-md">
        You don&apos;t have permission to open this page. Use the sidebar to go somewhere you can
        access, or contact an administrator if you need access.
      </p>
    </div>
  );
}

/**
 * Blocks the route unless the user has ANY of the tab's component permissions.
 * Direct URL navigation without permission redirects to the first allowed page,
 * or shows Access Denied when none are available.
 */
export function PermissionRoute({ permissions, children, fallback }) {
  const { pathname } = useLocation();
  const { canAny } = usePermissions();
  const { currentUser, permissions: granted } = useRole();

  const required = useMemo(
    () => effectiveRoutePermissions(pathname) ?? permissions,
    [pathname, permissions],
  );

  const normalizedPath = pathname.replace(/\/+$/, "") || "/";

  // No permission list configured for this route — allow (public shell content).
  if (!required?.length) return children;

  if (canAny(required)) return children;

  const redirectTo = fallback ?? firstAllowedPath(currentUser, granted);

  // Send them to a page they can open (never stay on the forbidden URL).
  if (redirectTo) {
    const redirectNorm = redirectTo.replace(/\/+$/, "") || "/";
    if (redirectNorm !== normalizedPath) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  if (!hasValidAccessToken()) {
    return <Navigate to={loginRedirectPath()} replace />;
  }

  // Authenticated but this URL (and maybe every URL) is forbidden.
  return <AccessDenied />;
}
